// ═══════════════════════════════════════════════════════════════
// PIXALLOY — CanvasArea Render Function
// ═══════════════════════════════════════════════════════════════
// Main canvas viewport with rulers, guides, and overlay.
// Called inline from PixAlloy.jsx as renderCanvasArea(p).
// ═══════════════════════════════════════════════════════════════

import { V } from "../theme.js";
import { T } from "../engine/index.js";
import { CURS } from "../tools/toolDefs.jsx";
import { renderStatusBar } from "./StatusBar.jsx";

export function renderCanvasArea(p) {
  const { showRulers, panState, cw, ch, vpContainerRef, vpRef, cssFilter,
    cvs, cvsRefCb, ovlRefCb, sessionId, onDown, onMove, onUp, onLeave, onDblClick,
    guides, setGuides, nearGuide,
    toolLabel, curPos, zmDisplay, rotDisplay, wasmCaps, layers, E,
    tool, zm, altHeld, penHover, rbHasSel, tfmCursor,
    fi, guideDragRef, radial, setRadial, setSelCtxMenu } = p;
  return (
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {/* Canvas viewport with rulers */}
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {/* Top ruler */}
            {showRulers&&<div style={{display:"flex",flexShrink:0}}>
              <div style={{width:20,height:20,background:"#1a1a1a",borderRight:`1px solid ${V.panelBorder}`,borderBottom:`1px solid ${V.panelBorder}`,flexShrink:0}}/>
              <canvas ref={el=>{
                if(!el)return;const rx=el.getContext("2d",{willReadFrequently:true});const w=el.width=el.parentElement.clientWidth-21;el.height=20;
                rx.fillStyle="#1a1a1a";rx.fillRect(0,0,w,20);
                rx.fillStyle=V.ghost;rx.font="8px monospace";rx.textBaseline="bottom";
                const zm2=panState.current.zoom,px2=panState.current.panX;
                const step=zm2>=2?10:zm2>=0.5?50:100;
                for(let i=0;i<cw;i+=step){
                  const sx=(i*zm2)+px2+(w/2)-(cw*zm2/2);
                  if(sx<0||sx>w)continue;
                  rx.fillStyle=V.ghost;rx.fillText(String(i),sx+2,18);
                  rx.fillStyle=V.panelBorder2;rx.fillRect(sx,14,1,6);
                }
                // Minor ticks
                const minor=step/5;
                for(let i=0;i<cw;i+=minor){const sx=(i*zm2)+px2+(w/2)-(cw*zm2/2);if(sx>=0&&sx<=w){rx.fillStyle="#333";rx.fillRect(sx,17,1,3);}}
              }} style={{flex:1,height:20,display:"block",borderBottom:`1px solid ${V.panelBorder}`,cursor:"s-resize"}}
                onMouseDown={e=>{
                  e.preventDefault();guideDragRef.current={type:"h",pos:0};
                  const screenToCanvasY=(clientY)=>{
                    const c=cvs.current;if(!c)return 0;
                    const r=c.getBoundingClientRect();
                    return (clientY-r.top)*(c.height/r.height);
                  };
                  const onM=e2=>{guideDragRef.current={type:"h",pos:Math.round(screenToCanvasY(e2.clientY))};};
                  const onU=e2=>{
                    const cy=Math.round(screenToCanvasY(e2.clientY));
                    if(cy>=0&&cy<=ch)setGuides(g=>[...g,{type:"h",pos:cy}]);
                    guideDragRef.current=null;document.removeEventListener("mousemove",onM);document.removeEventListener("mouseup",onU);
                  };
                  document.addEventListener("mousemove",onM);document.addEventListener("mouseup",onU);
                }}/>
            </div>}
            <div style={{flex:1,display:"flex",overflow:"hidden"}}>
              {/* Left ruler */}
              {showRulers&&<canvas ref={el=>{
                if(!el)return;const rx=el.getContext("2d",{willReadFrequently:true});el.width=20;const h=el.height=el.parentElement.clientHeight;
                rx.fillStyle="#1a1a1a";rx.fillRect(0,0,20,h);
                rx.save();rx.fillStyle=V.ghost;rx.font="8px monospace";rx.textBaseline="top";
                const zm2=panState.current.zoom,py2=panState.current.panY;
                const step=zm2>=2?10:zm2>=0.5?50:100;
                for(let i=0;i<ch;i+=step){
                  const sy=(i*zm2)+py2+(h/2)-(ch*zm2/2);
                  if(sy<0||sy>h)continue;
                  rx.save();rx.translate(2,sy+2);rx.fillText(String(i),0,0);rx.restore();
                  rx.fillStyle=V.panelBorder2;rx.fillRect(14,sy,6,1);rx.fillStyle=V.ghost;
                }
                const minor=step/5;
                for(let i=0;i<ch;i+=minor){const sy=(i*zm2)+py2+(h/2)-(ch*zm2/2);if(sy>=0&&sy<=h){rx.fillStyle="#333";rx.fillRect(17,sy,3,1);}}
                rx.restore();
              }} style={{width:20,display:"block",borderRight:`1px solid ${V.panelBorder}`,flexShrink:0,cursor:"e-resize"}}
                onMouseDown={e=>{
                  e.preventDefault();guideDragRef.current={type:"v",pos:0};
                  const screenToCanvasX=(clientX)=>{
                    const c=cvs.current;if(!c)return 0;
                    const r=c.getBoundingClientRect();
                    return (clientX-r.left)*(c.width/r.width);
                  };
                  const onM=e2=>{guideDragRef.current={type:"v",pos:Math.round(screenToCanvasX(e2.clientX))};};
                  const onU=e2=>{
                    const cx2=Math.round(screenToCanvasX(e2.clientX));
                    if(cx2>=0&&cx2<=cw)setGuides(g=>[...g,{type:"v",pos:cx2}]);
                    guideDragRef.current=null;document.removeEventListener("mousemove",onM);document.removeEventListener("mouseup",onU);
                  };
                  document.addEventListener("mousemove",onM);document.addEventListener("mouseup",onU);
                }}/>}
              {/* Viewport */}
              <div ref={vpContainerRef} data-viewport style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",position:"relative",background:"radial-gradient(ellipse at 60% 40%, rgba(190,27,27,0.04) 0%, transparent 60%), #111",touchAction:"none"}}
            onContextMenu={e=>{e.preventDefault();
              if(rbHasSel()){setSelCtxMenu({x:e.clientX,y:e.clientY});setRadial(null);}
              else{setRadial({x:e.clientX,y:e.clientY,hasSel:false});setSelCtxMenu(null);}
            }}
            onDragOver={e=>{e.preventDefault();e.dataTransfer.dropEffect="copy";}}
            onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){const dt=new DataTransfer();dt.items.add(f);fi.current.files=dt.files;fi.current.dispatchEvent(new Event("change",{bubbles:true}));}}}>
            <div ref={vpRef} style={{transformOrigin:"center",willChange:"transform",boxShadow:"0 0 0 1px rgba(46,46,46,1), 0 8px 48px rgba(0,0,0,0.6)",position:"relative",width:cw,height:ch}}>
              <canvas key={"mc"+sessionId} ref={cvsRefCb} width={cw} height={ch}
                style={{display:"block",width:cw,height:ch,imageRendering:zm>3?"pixelated":"auto",filter:cssFilter}}/>
              <canvas key={"ov"+sessionId} ref={ovlRefCb} width={cw} height={ch}
                style={{position:"absolute",left:0,top:0,display:"block",imageRendering:zm>3?"pixelated":"auto",touchAction:"none",cursor:tfmCursor||(nearGuide==="h"?"ns-resize":nearGuide==="v"?"ew-resize":(tool===T.PEN&&penHover==="close")?"pointer":(tool===T.PEN&&penHover==="segment")?"copy":(tool===T.PEN&&penHover==="corner")?"crosshair":(tool===T.PEN&&penHover)?"default":(tool===T.ZOOM&&altHeld)?"zoom-out":(tool===T.RUBBER_STAMP&&altHeld)?"copy":CURS[tool]??"default")}}
                onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onLeave} onMouseLeave={onLeave} onDoubleClick={onDblClick}/>
            </div>
            </div>
          </div>
          </div>

          {/* Status bar */}
          {renderStatusBar({ toolLabel, cw, ch, curPos, zmDisplay, rotDisplay, wasmCaps, layers, E })}
        </div>

  );
}
